import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ImageField, MultiImageField, VideoField } from "@/components/admin/ImageField";
import { Plus, PencilSimple, Trash, X, Star } from "@phosphor-icons/react";

const EMPTY = {
  title: "", category: "", location: "", area: "", year: "", status: "Completed",
  excerpt: "", description: "", cover_image: "", images: [], videos: [], featured: false, order: 0,
  seo_title: "", seo_description: "",
};

export default function ProjectsPanel() {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/projects").then(({ data }) => setProjects(data));
    api.get("/categories").then(({ data }) => setCategories(data));
  };
  useEffect(load, []);

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/admin/projects/${form.id}`, form);
      else await api.post("/admin/projects", form);
      toast.success("Project saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("Project deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif text-3xl font-light">Projects</h2>
          <p className="text-sm text-muted-foreground mt-1">{projects.length} projects · shown on the site</p>
        </div>
        <button
          data-testid="new-project-btn"
          onClick={() => setEditing({ ...EMPTY })}
          className="inline-flex items-center gap-2 bg-foreground text-background text-xs uppercase tracking-[0.15em] px-5 py-3 hover:bg-accent transition-colors"
        >
          <Plus size={16} /> New Project
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {projects.map((p) => (
          <div key={p.id} className="border border-border bg-card group" data-testid={`admin-project-${p.slug}`}>
            <div className="relative aspect-[4/3] bg-muted overflow-hidden">
              {p.cover_image && <img src={p.cover_image.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${p.cover_image}` : p.cover_image} alt={p.title} className="h-full w-full object-cover" />}
              {p.featured && <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-background/90 text-[10px] uppercase tracking-wide px-2 py-1"><Star size={11} weight="fill" /> Featured</span>}
            </div>
            <div className="p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-accent">{p.category_name || "—"}</p>
              <h3 className="mt-1 font-serif text-xl">{p.title}</h3>
              <p className="text-xs text-muted-foreground">{p.location}</p>
              <div className="mt-4 flex gap-2">
                <button onClick={() => setEditing(p)} data-testid={`edit-${p.slug}`} className="flex-1 inline-flex items-center justify-center gap-1 text-xs border border-border py-2 hover:bg-secondary transition-colors">
                  <PencilSimple size={14} /> Edit
                </button>
                <button onClick={() => remove(p.id)} className="inline-flex items-center justify-center px-3 border border-border text-destructive hover:bg-destructive hover:text-destructive-foreground transition-colors">
                  <Trash size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <ProjectForm
          initial={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function ProjectForm({ initial, categories, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl h-full bg-background overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="project-form">
        <div className="sticky top-0 bg-background border-b border-border px-8 py-5 flex items-center justify-between z-10">
          <h3 className="font-serif text-2xl">{form.id ? "Edit Project" : "New Project"}</h3>
          <button onClick={onClose}><X size={22} /></button>
        </div>
        <div className="p-8 space-y-6">
          <Field label="Title"><input data-testid="pf-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <select data-testid="pf-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                <option value="">Select…</option>
                {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
                <option>Completed</option><option>Ongoing</option><option>Coming Soon</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Location"><input value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} /></Field>
            <Field label="Area"><input value={form.area} onChange={(e) => set("area", e.target.value)} className={inputCls} /></Field>
            <Field label="Year"><input value={form.year} onChange={(e) => set("year", e.target.value)} className={inputCls} /></Field>
          </div>
          <Field label="Excerpt (short)"><input value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} className={inputCls} /></Field>
          <Field label="Description"><textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></Field>
          <ImageField label="Cover Image" value={form.cover_image} onChange={(v) => set("cover_image", v)} testid="pf-cover" />
          <MultiImageField label="Gallery Images" value={form.images} onChange={(v) => set("images", v)} />
          <VideoField
            label="Feature Video — upload directly, or paste a link below (used as the project's video)"
            testid="pf-video-upload"
            value={(form.videos || [])[0] || ""}
            onChange={(url) => {
              const rest = (form.videos || []).slice(1);
              set("videos", url ? [url, ...rest] : rest);
            }}
          />
          <Field label="Backup video links (optional — additional URLs, one per line: mp4, YouTube or Vimeo)">
            <textarea
              rows={3}
              data-testid="pf-videos"
              value={(form.videos || []).slice(1).join("\n")}
              onChange={(e) => {
                const primary = (form.videos || [])[0] || "";
                const lines = e.target.value.split("\n").map((s) => s.trim()).filter(Boolean);
                set("videos", primary ? [primary, ...lines] : lines);
              }}
              className={inputCls}
              placeholder="https://example.com/tour.mp4&#10;https://youtu.be/xxxxxxxxxxx"
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Order"><input type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className={inputCls} /></Field>
            <label className="flex items-center gap-3 mt-6 cursor-pointer">
              <input type="checkbox" data-testid="pf-featured" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-[hsl(25_25%_45%)]" />
              <span className="text-sm">Feature on homepage</span>
            </label>
          </div>
          <div className="pt-4 border-t border-border">
            <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-4">SEO (optional)</p>
            <div className="space-y-4">
              <Field label="SEO Title"><input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className={inputCls} /></Field>
              <Field label="SEO Description"><textarea rows={2} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} className={inputCls} /></Field>
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-background border-t border-border px-8 py-5 flex gap-3">
          <button onClick={() => onSave(form)} data-testid="pf-save" className="flex-1 bg-foreground text-background text-xs uppercase tracking-[0.2em] py-4 hover:bg-accent transition-colors">Save Project</button>
          <button onClick={onClose} className="px-8 border border-border text-xs uppercase tracking-[0.2em]">Cancel</button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-transparent border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground transition-colors";
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{label}</label>
      {children}
    </div>
  );
}
